import React, {
  createContext,
  CSSProperties,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// ============================================================================
// Constants
// ============================================================================

const FADE_IN_MS = 300;
const FADE_OUT_MS = 100;
const MOUNT_DELAY_MS = 10;
const HOVER_GRACE_PERIOD_MS = 100;
const TOOLTIP_OFFSET_PX = 8;

const BASE_STYLES: CSSProperties = {
  position: 'absolute',
  background: '#000',
  color: '#fff',
  padding: '5px 10px',
  borderRadius: '5px',
  fontSize: '15px',
  zIndex: 9999,
};

const TRANSFORM_BY_PLACEMENT: Record<Placement, string> = {
  top: 'translate(-50%, -100%)',
  right: 'translate(0, -50%)',
  bottom: 'translate(-50%, 0)',
  left: 'translate(-100%, -50%)',
};

// ============================================================================
// Types
// ============================================================================

type Placement = 'top' | 'right' | 'bottom' | 'left';

interface Position {
  top: number;
  left: number;
}

interface TooltipPortalProps {
  targetId: string;
  content: ReactNode;
  placement?: Placement;
  styleOverrides?: CSSProperties;
  locationId?: string;
  limitWidth?: number;
  persistent?: boolean;
}

// ============================================================================
// Tooltip Manager Context (replaces global mutable state)
// ============================================================================

interface TooltipManagerContextValue {
  requestShow: (id: string, hide: () => void) => void;
  notifyHidden: (id: string) => void;
}

const TooltipManagerContext = createContext<TooltipManagerContextValue | null>(null);

export const TooltipManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const activeRef = useRef<{ id: string; hide: () => void } | null>(null);

  const requestShow = useCallback((id: string, hide: () => void) => {
    if (activeRef.current && activeRef.current.id !== id) {
      activeRef.current.hide();
    }
    activeRef.current = { id, hide };
  }, []);

  const notifyHidden = useCallback((id: string) => {
    if (activeRef.current?.id === id) {
      activeRef.current = null;
    }
  }, []);

  return (
    <TooltipManagerContext.Provider value={{ requestShow, notifyHidden }}>{children}</TooltipManagerContext.Provider>
  );
};

const useTooltipManager = () => useContext(TooltipManagerContext);

// ============================================================================
// Position calculation hook
// ============================================================================

const useTooltipPosition = (targetId: string, locationId: string | undefined, placement: Placement) => {
  const calculatePosition = useCallback((): Position | null => {
    const target = document.getElementById(targetId);
    const anchor = locationId ? document.getElementById(locationId) : target;
    if (!anchor) return null;

    const rect = anchor.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    const positions: Record<Placement, Position> = {
      top: {
        top: rect.top + scrollY - TOOLTIP_OFFSET_PX,
        left: rect.left + scrollX + rect.width / 2,
      },
      right: {
        top: rect.top + scrollY + rect.height / 2,
        left: rect.left + scrollX + rect.width + TOOLTIP_OFFSET_PX,
      },
      bottom: {
        top: rect.top + scrollY + rect.height + TOOLTIP_OFFSET_PX,
        left: rect.left + scrollX + rect.width / 2,
      },
      left: {
        top: rect.top + scrollY + rect.height / 2,
        left: rect.left + scrollX - TOOLTIP_OFFSET_PX,
      },
    };

    return positions[placement];
  }, [targetId, locationId, placement]);

  return calculatePosition;
};

// ============================================================================
// Visibility state hook
// ============================================================================

type VisibilityState = 'hidden' | 'mounting' | 'visible' | 'unmounting';

const useTooltipVisibility = (tooltipId: string) => {
  const [state, setState] = useState<VisibilityState>('hidden');
  const manager = useTooltipManager();
  const unmountTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const mountTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const show = useCallback(() => {
    clearTimeout(unmountTimeoutRef.current);
    clearTimeout(mountTimeoutRef.current);

    manager?.requestShow(tooltipId, () => hide());

    setState('mounting');
    mountTimeoutRef.current = setTimeout(() => setState('visible'), MOUNT_DELAY_MS);
  }, [tooltipId, manager]);

  const hide = useCallback(() => {
    clearTimeout(mountTimeoutRef.current);

    setState('unmounting');
    manager?.notifyHidden(tooltipId);

    unmountTimeoutRef.current = setTimeout(() => setState('hidden'), FADE_OUT_MS);
  }, [tooltipId, manager]);

  useEffect(() => {
    return () => {
      clearTimeout(unmountTimeoutRef.current);
      clearTimeout(mountTimeoutRef.current);
      manager?.notifyHidden(tooltipId);
    };
  }, [tooltipId, manager]);

  const isMounted = state !== 'hidden';
  const isVisible = state === 'visible';

  return { isMounted, isVisible, show, hide };
};

// ============================================================================
// Target event listener hook
// ============================================================================

const useTargetEvents = (
  targetId: string,
  persistent: boolean,
  onEnter: () => void,
  onLeave: () => void,
  setIsOverTarget: (value: boolean) => void,
) => {
  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) return;

    const handleEnter = () => {
      setIsOverTarget(true);
      onEnter();
    };

    const handleLeave = () => {
      setIsOverTarget(false);
      if (!persistent) {
        onLeave();
      }
    };

    target.addEventListener('mouseenter', handleEnter);
    target.addEventListener('mouseleave', handleLeave);

    return () => {
      target.removeEventListener('mouseenter', handleEnter);
      target.removeEventListener('mouseleave', handleLeave);
    };
  }, [targetId, persistent, onEnter, onLeave, setIsOverTarget]);
};

// ============================================================================
// Persistent mode hover hook
// ============================================================================

const usePersistentHover = (
  persistent: boolean,
  isMounted: boolean,
  isOverTarget: boolean,
  isOverTooltip: boolean,
  hide: () => void,
) => {
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!persistent) return;

    if (!isOverTarget && !isOverTooltip && isMounted) {
      hideTimeoutRef.current = setTimeout(hide, HOVER_GRACE_PERIOD_MS);
    } else {
      clearTimeout(hideTimeoutRef.current);
    }

    return () => clearTimeout(hideTimeoutRef.current);
  }, [persistent, isMounted, isOverTarget, isOverTooltip, hide]);
};

// ============================================================================
// Style builders
// ============================================================================

const buildStyles = (
  position: Position,
  placement: Placement,
  isVisible: boolean,
  limitWidth: number | undefined,
  persistent: boolean,
  styleOverrides: CSSProperties,
): CSSProperties => {
  const widthStyles: CSSProperties = limitWidth
    ? { maxWidth: `${limitWidth}px`, whiteSpace: 'normal', wordWrap: 'break-word' }
    : { whiteSpace: 'nowrap' };

  return {
    ...BASE_STYLES,
    ...widthStyles,
    pointerEvents: persistent ? 'auto' : 'none',
    top: position.top,
    left: position.left,
    transform: TRANSFORM_BY_PLACEMENT[placement],
    opacity: isVisible ? 1 : 0,
    transition: `opacity ${isVisible ? FADE_IN_MS : FADE_OUT_MS}ms ease-in-out`,
    ...styleOverrides,
  };
};

// ============================================================================
// Main Component
// ============================================================================

const TooltipPortal: React.FC<TooltipPortalProps> = ({
  targetId,
  content,
  placement = 'top',
  locationId,
  styleOverrides = {},
  limitWidth,
  persistent = false,
}) => {
  const tooltipId = useId();
  const [position, setPosition] = useState<Position | null>(null);
  const [isOverTarget, setIsOverTarget] = useState(false);
  const [isOverTooltip, setIsOverTooltip] = useState(false);

  const calculatePosition = useTooltipPosition(targetId, locationId, placement);
  const { isMounted, isVisible, show, hide } = useTooltipVisibility(tooltipId);

  const handleShow = useCallback(() => {
    const pos = calculatePosition();
    if (pos) {
      setPosition(pos);
      show();
    }
  }, [calculatePosition, show]);

  useTargetEvents(targetId, persistent, handleShow, hide, setIsOverTarget);
  usePersistentHover(persistent, isMounted, isOverTarget, isOverTooltip, hide);

  if (!isMounted || !position) return null;

  return createPortal(
    <div
      onMouseEnter={() => persistent && setIsOverTooltip(true)}
      onMouseLeave={() => persistent && setIsOverTooltip(false)}
      style={buildStyles(position, placement, isVisible, limitWidth, persistent, styleOverrides)}
    >
      <span className={`tooltip-portal ${placement}`} />
      {content}
    </div>,
    document.body,
  );
};

export default TooltipPortal;
